import axios from 'axios';
import { jsonp } from '@ifeng-fe/ui_base';
import md5 from 'md5';
import COS from 'cos-js-sdk-v5';

// apiUrl为webpack注入的全局变量
/* eslint-disable no-undef */
let apiBaseUrl = apiUrl;
/* eslint-enable no-undef */

if (typeof window !== 'undefined' && /\.shank\.ifeng\.com/.test(window.location.hostname)) {
    apiBaseUrl = '/api';
}

const UPLOAD_TOKEN_URL = 'https://d.shankapi.ifeng.com/api/fts/get/upload/token';
const MAX_UPLOAD_FILES = 5;
const ALLOWED_UPLOAD_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']);

// jsonp 接口请求示例 ---- start (开发时请删除示例代码)
// jsonp 接口请求示例 ---- start (开发时请删除示例代码)
// jsonp 接口请求示例 ---- start (开发时请删除示例代码)
const getWeatherData = async city => {
    return await jsonp(`https://api.iclient.ifeng.com/newH5Weather?key=weather&value=${city}`);
};

const createJsonpCallbackName = (str, num) => {
    num = num ? num : 0;
    let jsonpCallbackName = `_${md5(`${str}_${num}`)}`;

    /* eslint-disable */
    while (typeof window === 'object' && window[jsonpCallbackName]) {
        /* eslint-enable */
        num++;
        jsonpCallbackName = `_${md5(`${str}_${num}`)}`;
    }

    return jsonpCallbackName;
};

const getMyStockData = async num => {
    return await jsonp('//apiapp.finance.ifeng.com/mystock/get', {
        data: { num },
        jsonpCallback: createJsonpCallbackName('getMyStockData'),
        cache: false,
    });
};

// jsonp 接口请求示例 ---- end  (开发时请删除示例代码)
// jsonp 接口请求示例 ---- end  (开发时请删除示例代码)
// jsonp 接口请求示例 ---- end  (开发时请删除示例代码)

/**
 * 凤凰投诉表单提交 - 仅负责发送请求，数据以 JSON 对象形式传入 body
 * @param {Object} data - 表单数据对象
 * @param {string} data.nickname - 姓名
 * @param {string} data.phone - 手机号
 * @param {string} data.cmp_target - 投诉对象
 * @param {string} data.cmp_issue - 诉求问题
 * @param {string} data.cmp_detail - 投诉详情
 * @param {string[]} data.images - 图片 base64 或 URL 数组
 * @param {number} data.image_hidden - 图片是否隐藏 1=隐藏 0=显示
 */
const submitComplaint = async data => {
    try {
        const response = await axios.post(
            'https://d.shankapi.ifeng.com/api/fts/cmp/create',
            data,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    } catch (err) {
        const msg =
            err.response?.data?.message ||
            (typeof err.response?.data === 'string' ? err.response.data : null) ||
            err.message;
        throw new Error(msg || '请求失败');
    }
};

/**
 * 凤凰投诉列表 - JSONP 接口
 * @param {number} lastId - 首屏传 0，非首屏传上次返回的 last_cmp_id
 * @returns {Promise<{list: Array, last_cmp_id: number}>}
 */
const getComplaintList = async lastId => {
    const callbackName = createJsonpCallbackName('getComplaintList');
    const url = `https://shankapi.ifeng.com/api/fts/cmp/list/${lastId || 0}/${callbackName}`;
    const res = await jsonp(url, {
        cache: false,
        jsonpCallback: callbackName,
    });
    return res;
};

/**
 * 凤凰投诉详情 - JSONP 接口
 * @param {string|number} cmpId - 投诉ID，从详情页 URL 获取
 * @returns {Promise<Object>}
 */
const getComplaintDetail = async cmpId => {
    const callbackName = createJsonpCallbackName('getComplaintDetail');
    const url = `https://shankapi.ifeng.com/api/fts/cmp/${cmpId || 0}/info/${callbackName}`;
    const res = await jsonp(url, {
        cache: false,
        jsonpCallback: callbackName,
    });
    return res;
};

const getExtByFileName = fileName => {
    if (!fileName || typeof fileName !== 'string') {
        return '';
    }
    const parts = fileName.toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
};

const getUploadToken = async exts => {
    try {
        const response = await axios.get(UPLOAD_TOKEN_URL, {
            params: {
                ext: exts.join(','),
            },
        });
        const raw = response.data || {};
        const data = raw?.data || raw;
        if (raw?.code || data?.code) {
            throw new Error(raw?.message || data?.message || '获取上传凭证失败');
        }
        return data;
    } catch (err) {
        const msg =
            err.response?.data?.message ||
            (typeof err.response?.data === 'string' ? err.response.data : null) ||
            err.message;
        throw new Error(msg || '获取上传凭证失败');
    }
};

const uploadImagesToCos = async files => {
    if (!Array.isArray(files) || files.length === 0) {
        return [];
    }
    if (files.length > MAX_UPLOAD_FILES) {
        throw new Error('最多 5 张图片');
    }

    const exts = files.map(file => {
        const ext = getExtByFileName(file?.name);
        if (!ext) {
            throw new Error(`无法识别文件扩展名：${file?.name || '未知文件'}`);
        }
        if (!ALLOWED_UPLOAD_EXTS.has(ext)) {
            throw new Error(`非法扩展名 ${ext}，允许：jpg、jpeg、png、gif、webp、bmp`);
        }
        return ext;
    });

    const tokenData = await getUploadToken(exts);

    const { credentials, bucket, region, uploadKeys = [] } = tokenData;

    if (!credentials?.tmpSecretId || !credentials?.tmpSecretKey || !credentials?.sessionToken) {
        throw new Error('上传凭证不完整');
    }
    if (!bucket || !region) {
        throw new Error('上传配置不完整');
    }
    if (uploadKeys.length !== files.length) {
        throw new Error('上传路径数量与图片数量不一致');
    }

    const cos = new COS({
        SecretId: credentials.tmpSecretId,
        SecretKey: credentials.tmpSecretKey,
        SecurityToken: credentials.sessionToken,
    });

    await Promise.all(
        uploadKeys.map((key, index) => {
            return new Promise((resolve, reject) => {
                cos.putObject(
                    {
                        Bucket: bucket,
                        Region: region,
                        Key: key,
                        Body: files[index],
                    },
                    (err) => {
                        if (err) {
                            reject(err);
                            return;
                        }
                        resolve();
                    }
                );
            });
        })
    );

    return uploadKeys;
};

export {
    getMyStockData,
    getWeatherData,
    submitComplaint,
    getComplaintList,
    getComplaintDetail,
    uploadImagesToCos,
};
